import _ from 'lodash';
import '../styles/main.css';

/**
 * Ohm's Law Simulation - Core Logic
 * Handles interactive circuit calculations, electron animation,
 * and dynamic SVG updates.
 */

// --- Configuration & Constants ---
const BASE_DIMENSIONS = { width: 1280, height: 720 };
const VOLTAGE_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const RESISTANCE_VALUES = [100, 120, 150, 180, 220, 270, 330, 390, 470, 560, 680, 820, 1000];

const BATTERY_CONFIG = {
    anchorX: 347.73, // Fixed right-side coordinate of the battery fill
    maxWidth: 125,
    minWidth: 10,
};

const COLORS = {
    bulbOff: '#eaeaea',
    bulbOn: '#f9b02d',
};

// Physics Constants
const MAX_POWER = 1.44; // Calculated as: Max Voltage (12) / Min Resistance (100) * Max Voltage (12)
const SPEED_MULTIPLIER = 40;

// State Variables
let progress = 0;
let currentSpeed = 0;

// --- DOM Elements ---
const elements = {
    stage: document.getElementById('ohms-law-simulation'),
    ampsDisplay: document.getElementById('amps'),

    // Controls
    vSlider: document.getElementById('voltage-slider'),
    vBadge: document.getElementById('voltage-display'),
    rSlider: document.getElementById('resistance-slider'),
    rBadge: document.getElementById('resistance-display'),

    // Formula Components
    vCurrentFormula: document.getElementById('voltage'),
    vPowerFormula: document.getElementById('p-voltage'),
    rCurrentFormula: document.getElementById('resistance'),
    iPowerFormula: document.getElementById('p-current'),
    wattPowerFormula: document.getElementById('watt'),

    // SVG Components
    electrons: document.querySelectorAll('.electron'),
    circuitPath: document.getElementById('circuit-path'),
    lightbulb: document.getElementById('lightbulb'),
    lightbulbBox: document.getElementById('lightbulb-box'),
    batteryLevel: document.getElementById('battery-level'),
    resistor: document.getElementById('resistor'),
};

// --- Utilities & Math ---

/**
 * Linearly interpolates between two hex colors based on a factor.
 * @param {string} color1 - Start hex color
 * @param {string} color2 - End hex color
 * @param {number} factor - Transition value (0 to 1)
 * @returns {string} - Resulting rgb() string
 */
function interpolateColor(color1, color2, factor) {
    const parse = (c) => [parseInt(c.substring(1, 3), 16), parseInt(c.substring(3, 5), 16), parseInt(c.substring(5, 7), 16)];

    const [r1, g1, b1] = parse(color1);
    const [r2, g2, b2] = parse(color2);

    const r = Math.round(r1 + factor * (r2 - r1));
    const g = Math.round(g1 + factor * (g2 - g1));
    const b = Math.round(b1 + factor * (b2 - b1));

    return `rgb(${r}, ${g}, ${b})`;
}

/** Handles responsive scaling of the simulation stage */
function handleViewPortResize() {
    const scale = Math.min(window.innerWidth / BASE_DIMENSIONS.width, window.innerHeight / BASE_DIMENSIONS.height);
    elements.stage.style.transform = `scale(${scale})`;
}

// --- Simulation Logic ---

/**
 * Updates the physical state of the simulation:
 * 1. Calculates Current (I) and Power (W)
 * 2. Triggers visual updates for battery, resistor, and bulb
 */
function updateSimulation() {
    const V = Number(elements.vCurrentFormula.innerText);
    const R = Number(elements.rCurrentFormula.innerText);

    if (R <= 0) {
        elements.ampsDisplay.innerText = '0.000';
        return;
    }

    // Physics Calculations
    const I = V / R;
    const W = V * I;
    const powerFactor = Math.min(W / MAX_POWER, 1);

    // Update UI Displays
    elements.ampsDisplay.innerText = I.toFixed(3);
    elements.iPowerFormula.innerText = I.toFixed(3);
    elements.wattPowerFormula.innerText = W.toFixed(3);

    // Update Animation Speed (Square root provides smoother visual scaling)
    currentSpeed = Math.sqrt(I) * SPEED_MULTIPLIER;

    // Update Visual Components
    updateResistorVisual(R);
    updateBatteryVisual(V);
    updateBulbVisual(powerFactor);
}

/** Updates bulb color and glow opacity based on power factor */
function updateBulbVisual(factor) {
    elements.lightbulb.style.fill = interpolateColor(COLORS.bulbOff, COLORS.bulbOn, factor);
    // Glow effect (lightbulbBox) is capped at 20% opacity for visual balance
    elements.lightbulbBox.style.opacity = Math.min(factor, 0.2);
}

/** Maps current voltage to battery fill width and position */
function updateBatteryVisual(voltage) {
    const percentage = (voltage - 1) / 11; // Normalize 1-12 range to 0-1
    const newWidth = BATTERY_CONFIG.minWidth + percentage * (BATTERY_CONFIG.maxWidth - BATTERY_CONFIG.minWidth);

    // Anchor to the right by shifting X as width grows
    const newX = BATTERY_CONFIG.anchorX - newWidth;

    elements.batteryLevel.setAttribute('width', newWidth);
    elements.batteryLevel.setAttribute('x', newX);
}

/** Switches CSS class on resistor to change its color bands */
function updateResistorVisual(resistance) {
    elements.resistor.setAttribute('class', `r-${resistance}`);
}

// --- Animation Loop ---

/**
 * Animates electrons along a rectangular path.
 * Electrons move along segments: Top -> Right -> Bottom -> Left.
 */
function animateElectrons() {
    if (currentSpeed > 0 && elements.circuitPath && elements.electrons.length > 0) {
        const pathWidth = Number(elements.circuitPath.getAttribute('width'));
        const pathHeight = Number(elements.circuitPath.getAttribute('height'));
        const startX = Number(elements.circuitPath.getAttribute('x'));
        const startY = Number(elements.circuitPath.getAttribute('y'));
        const perimeter = 2 * (pathWidth + pathHeight);

        // Update global offset
        progress = (progress + currentSpeed) % perimeter;

        elements.electrons.forEach((electron, index) => {
            // Space electrons evenly around the perimeter
            const spacing = perimeter / elements.electrons.length;
            const electronPos = (progress + index * spacing) % perimeter;

            let x, y;

            if (electronPos < pathWidth) {
                // Segment 1: Top (Left to Right)
                x = startX + electronPos;
                y = startY;
            } else if (electronPos < pathWidth + pathHeight) {
                // Segment 2: Right (Top to Bottom)
                x = startX + pathWidth;
                y = startY + (electronPos - pathWidth);
            } else if (electronPos < 2 * pathWidth + pathHeight) {
                // Segment 3: Bottom (Right to Left)
                x = startX + pathWidth - (electronPos - (pathWidth + pathHeight));
                y = startY + pathHeight;
            } else {
                // Segment 4: Left (Bottom to Top)
                x = startX;
                y = startY + pathHeight - (electronPos - (2 * pathWidth + pathHeight));
            }

            electron.setAttribute('cx', x);
            electron.setAttribute('cy', y);
        });
    }

    requestAnimationFrame(animateElectrons);
}

// --- Initialization ---

/**
 * Binds events to sliders and updates formula labels.
 * @param {HTMLElement} slider - Input range element
 * @param {Array} values - Values mapped to slider index
 * @param {HTMLElement} badge - Text display for slider
 * @param {HTMLElement} formula - Text display for Ohm formula
 * @param {HTMLElement|null} powerFormula - Optional text display for Power formula
 */
function setupSlider(slider, values, badge, formula, powerFormula = null) {
    slider.addEventListener('input', (e) => {
        const val = values[e.target.value];
        badge.innerText = val;
        formula.innerText = val;
        slider.setAttribute('aria-valuenow', val);
        if (powerFormula) powerFormula.innerText = val;

        updateSimulation();
    });
}

const init = () => {
    // 1. Setup Listeners
    window.addEventListener('resize', _.throttle(handleViewPortResize, 50));
    setupSlider(elements.vSlider, VOLTAGE_VALUES, elements.vBadge, elements.vCurrentFormula, elements.vPowerFormula);
    setupSlider(elements.rSlider, RESISTANCE_VALUES, elements.rBadge, elements.rCurrentFormula);

    // 2. Set Initial State
    elements.vCurrentFormula.innerText = VOLTAGE_VALUES[elements.vSlider.value];
    elements.vPowerFormula.innerText = VOLTAGE_VALUES[elements.vSlider.value];
    elements.rCurrentFormula.innerText = RESISTANCE_VALUES[elements.rSlider.value];

    // 3. Start Loops
    handleViewPortResize();
    updateSimulation();
    animateElectrons();
};

init();
